import { useEffect } from 'react';
import type { ReaderBook } from '@/services/booksApi';
import { bufferScheduler } from '@/native/bufferScheduler';
import { isNativeApp } from '@/utils/platform';

interface UseBufferSchedulerArgs {
  /** True while a book is open and playable (mirrors the docked-player mount). */
  enabled: boolean;
  book: ReaderBook | null;
  voiceKey: string;
  currentChapterIndex: number;
  /** Elapsed time within the chapter, seconds. */
  positionSeconds: number;
  isPlaying: boolean;
}

/**
 * The playhead is reported this coarsely. The window is minutes deep and a
 * chapter is atomic, so a change of seconds can never change the plan, and
 * feeding every frame into the scheduler would only make it re-plan for
 * nothing.
 */
const POSITION_BUCKET_MS = 30_000;

/**
 * Feeds the player's state to the look-ahead buffer. Entirely gated on
 * isNativeApp(): there is no store on the web, and the scheduler itself
 * refuses to run there too.
 *
 * No cleanup that clears the book: the scheduler resets per-book state on a
 * book change, and clearing between renders would reset it every tick.
 */
export const useBufferScheduler = ({
  enabled,
  book,
  voiceKey,
  currentChapterIndex,
  positionSeconds,
  isPlaying,
}: UseBufferSchedulerArgs): void => {
  const active = isNativeApp() && enabled;
  const positionBucketMs = Math.floor((Math.max(0, positionSeconds) * 1000) / POSITION_BUCKET_MS) * POSITION_BUCKET_MS;

  useEffect(() => {
    if (!active) {
      bufferScheduler.update({ book: null, voiceKey: '', currentChapterIndex: 0, positionInChapterMs: 0, isPlaying: false });
      return;
    }
    bufferScheduler.update({ book, voiceKey, currentChapterIndex, positionInChapterMs: positionBucketMs, isPlaying });
  }, [active, book, voiceKey, currentChapterIndex, positionBucketMs, isPlaying]);
};
